// src/components/Home.jsx
import React, { useState, useEffect, useRef } from "react";
import "../styles/styles.css";
import { ReactComponent as PetfamLogo } from "../assets/PetfamLogo.svg";
import background from "../assets/BackgroundHome.webp";
import dog from "../assets/Dog.webp";
import cat from "../assets/Cat.webp";
import auxiliary from "../assets/VeterinaryAssistant.webp";
import gatoplan from "../assets/gatoplan.webp";
import { Link } from "react-router-dom";
import { useIntersection } from "../hooks/useIntersection";

import carrusel1 from "../assets/carrusel1.jpg";
import carrusel2 from "../assets/carrusel2.jpg";
import carrusel3 from "../assets/carrusel3.jpg";
import carrusel4 from "../assets/carrusel4.jpg";
import carrusel5 from "../assets/carrusel5.jpg";
import carrusel6 from "../assets/carrusel6.jpg";
import carrusel7 from "../assets/carrusel7.jpg";
import carrusel8 from "../assets/carrusel8.jpg";

// ✅ si ya tienes config, úsalo. Si no, deja esto comentado.
// import config from "../config";
// const API_BASE_URL = config.apiBaseUrl;

const Home = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef(null);

  // 🔹 Puntos de scroll
  const [activeSection, setActiveSection] = useState(0);
  const sectionsRef = useRef([]);

  // ✅ Responsive helpers
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const isMobileOrTablet = windowWidth <= 1024;

  // ✅ Carrusel de testimonios
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // =========================================================
  // ✅ Precio por geolocalización (IP)
  // - Colombia => COP
  // - Cualquier otro país => USD
  // =========================================================

  // ✅ Por defecto dejo USD (true) hasta saber el país
  const [showUSD, setShowUSD] = useState(true);

  // ✅ Precio del plan
  const PLAN_PRICE_COP = 9000;
  const PLAN_PRICE_USD = 2.99;

  const formatCOP = (n) =>
    n.toLocaleString("es-CO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const formatUSD = (n) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const planPriceText = showUSD
    ? `$${formatUSD(PLAN_PRICE_USD)}`
    : `$${formatCOP(PLAN_PRICE_COP)}`;

  const planPriceStrongText = showUSD
    ? `$${formatUSD(PLAN_PRICE_USD)} USD al mes`
    : `$${formatCOP(PLAN_PRICE_COP)} COP al mes`;

  // ✅ Endpoint real del geolocalizador (según tu captura)
  // https://qa.petfam.club/api/geo/country => {"country":"CO","ip":"..."}
  const GEO_URL = `/api/geo/country`;

  // ✅ Decide moneda por país (una vez al cargar)
  useEffect(() => {
    const controller = new AbortController();

    const decideCurrency = async () => {
      try {
        const res = await fetch(GEO_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: controller.signal,
          cache: "no-store",
        });

        if (!res.ok) return; // si falla, se queda en USD por defecto

        const data = await res.json();
        const country = String(data?.country || "").toUpperCase();

        // Colombia => COP
        setShowUSD(country !== "CO");
      } catch (e) {
        // si falla, se queda en USD por defecto
      }
    };

    decideCurrency();
    return () => controller.abort();
  }, []);

  const toggleMenu = () => setOpenMenu(!openMenu);

  // Cerrar menú si se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔹 Activar animaciones automáticas
  useIntersection(".fade-in");
  useIntersection(".zoom-in");
  useIntersection(".bounce-in");
  useIntersection(".carousel-in");

  // ✅ resize
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 🔹 Observador para las secciones y activar puntos
  useEffect(() => {
    const options = { root: null, threshold: 0.5 };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = sectionsRef.current.indexOf(entry.target);
        if (index !== -1) setActiveSection(index);
      });
    }, options);

    sectionsRef.current.forEach((section) => section && observer.observe(section));
    return () =>
      sectionsRef.current.forEach(
        (section) => section && observer.unobserve(section)
      );
  }, []);

  // 🔹 Scroll suave al hacer clic en punto
  const scrollToSection = (index) => {
    sectionsRef.current[index]?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(index);
  };

  // ✅ Data
  const trackImages = [
    { src: carrusel1, alt: "Carrusel 1" },
    { src: carrusel2, alt: "Carrusel 2" },
    { src: carrusel3, alt: "Carrusel 3" },
    { src: carrusel4, alt: "Carrusel 4" },
    { src: carrusel5, alt: "Carrusel 5" },
    { src: carrusel6, alt: "Carrusel 6" },
    { src: carrusel7, alt: "Carrusel 7" },
    { src: carrusel8, alt: "Carrusel 8" },
  ];

  const testimonials = [
    {
      title: "Fácil de usar",
      quote:
        "“La página es súper fácil. En minutos ya tenía todo listo y entendí el plan sin enredos.”",
      who: "Laura M. · Usuaria Petfam",
    },
    {
      title: "Tratamiento efectivo",
      quote:
        "“El medicamento formulado por el veterinario, le quitó por completo la alergía de los ojos a Milo.”",
      who: "Sebas R. · Papá de Milo",
    },
    {
      title: "Etólogo que sí funciona",
      quote:
        "“El etólogo fue un cambio total: mi perrito dejó de tirar de la correa y está mucho más tranquilo en casa.”",
      who: "Ana P. · Mamá de Bruno",
    },
    {
      title: "Soporte y acompañamiento",
      quote:
        "“Sentí apoyo real cuando tuve dudas y no tuve que esperar días para una respuesta.”",
      who: "María G. · Usuaria Petfam",
    },
    {
      title: "Todo organizado",
      quote:
        "“Historial, recordatorios y seguimiento. Es como tener la vida de mi mascota en un solo lugar.”",
      who: "Diego V. · Papá de Luna",
    },
  ];

  // ✅ Autoplay testimonios SOLO en móvil/tablet
  useEffect(() => {
    if (!isMobileOrTablet) return;

    const id = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 3000);

    return () => clearInterval(id);
  }, [isMobileOrTablet, testimonials.length]);

  const Stars = () => (
    <div className="pf-stars" aria-label="5 estrellas">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} viewBox="0 0 24 24" className="pf-star" aria-hidden="true">
          <path d="M12 17.27l-5.18 3.04 1.4-5.95-4.62-4 6.08-.52L12 4.5l2.32 5.34 6.08.52-4.62 4 1.4 5.95L12 17.27z" />
        </svg>
      ))}
    </div>
  );

  return (
    <>
      {/* 🔹 HEADER */}
      <header className="header">
        <div className="header-container">
          <div className="nav-left">
            <PetfamLogo className="logo" aria-label="Logo Petfam" />
          </div>

          <nav className="nav-center">
            <Link to="/" className="active underline-anim">
              Inicio
            </Link>
            <Link to="/servicios" className="underline-anim">
              Servicios
            </Link>
          </nav>

          <div className="nav-right">
            <div className="user-menu" ref={menuRef}>
              <button className="icon-user" onClick={toggleMenu} aria-label="Perfil">
                {/* 👤 Icono usuario */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="user-svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>

                {/* ⌄ Flecha */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="arrow-svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {openMenu && (
                <div className="dropdown-menu">
                  <Link to="/crear-cuenta" state={{ from: "home" }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      width="20"
                      height="20"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
                      />
                    </svg>
                    Suscribirse
                  </Link>

                  <hr />
                  <Link to="/login" state={{ from: "home" }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      width="20"
                      height="20"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                      />
                    </svg>
                    Iniciar sesión
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* 🔹 PUNTOS DE NAVEGACIÓN */}
      <div className="scroll-dots">
        {["Inicio", "Somos Petfam", "Plan", "Servicios", "Testimonios"].map(
          (label, i) => (
            <span
              key={i}
              title={label}
              className={`dot ${activeSection === i ? "active" : ""}`}
              onClick={() => scrollToSection(i)}
            ></span>
          )
        )}
      </div>

      {/* 🔹 HERO SECTION */}
      <section ref={(el) => (sectionsRef.current[0] = el)} className="hero fade-in">
        <h2 className="zoom-in">
          Porque no es solo una <span className="resaltado">mascota</span>, es parte de
          <br />
          <span className="resaltado fuerte">tu familia</span>
        </h2>

        <div className="hero-buttons bounce-in">
          <Link to="/crear-cuenta" state={{ from: "home" }} className="btn">
            Suscribirse
          </Link>
          <Link to="/login" state={{ from: "home" }} className="btn secondary">
            Iniciar sesión
          </Link>
        </div>

        <div className="hero-image zoom-in">
          <img src={background} alt="Veterinario con perrito" loading="lazy" />
        </div>
      </section>

      {/* 🔹 SOMOS PETFAM */}
      <section ref={(el) => (sectionsRef.current[1] = el)} className="somos fade-in">
        <div className="contenedor-somos">
          <div className="texto">
            <h1 className="underline-anim fade-in">Hola, somos Petfam</h1>
            <p className="fade-in">
              Ofrecemos servicios integrales de salud, bienestar y acompañamiento para
              mascotas y sus familias. Combinando tecnología y expertos en cada paso de
              su vida.
            </p>
          </div>

          <div className="mascotas">
            <img
              src={dog}
              alt="Perro"
              className="img-perro float zoom-in"
              loading="lazy"
              decoding="async"
            />
            <img
              src={cat}
              alt="Gato"
              className="img-gato float zoom-in"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* SVG SEPARADOR */}
      <div className="onda-inferior fade-in">
        <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#EDD4BA"
            fillOpacity="1"
            d="M0,64 C180,0 360,128 540,64 C720,0 900,128 1080,64 C1260,0 1440,128 1620,64 L1620,160 L0,160 Z"
          />
        </svg>
      </div>

      {/* ✅ PLAN */}
      <section ref={(el) => (sectionsRef.current[2] = el)} className="pf-plan">
        <div className="pf-plan-price zoom-in">{planPriceText}</div>

        <div className="pf-plan-container">
         <div className="pf-plan-text bounce-in">
  <p className="pf-plan-title-left">
    Todo lo que necesitas para cuidar la salud de tu mascota:
  </p>

  <ul className="pf-plan-list">
    <li>
      <span className="pf-plan-check">✓</span>
      <span>Orientación veterinaria cuando la necesites</span>
    </li>
    <li>
      <span className="pf-plan-check">✓</span>
      <span>Recordatorios de vacunas y cuidados</span>
    </li>
    <li>
      <span className="pf-plan-check">✓</span>
      <span>Historial médico digital siempre disponible</span>
    </li>
    <li>
      <span className="pf-plan-check">✓</span>
      <span>Toda la información de tu mascota en un solo lugar</span>
    </li>
  </ul>
</div>

          <div className="pf-plan-image zoom-in" aria-hidden="true">
            <img src={gatoplan} alt="Plan Petfam" loading="lazy" decoding="async" />
          </div>

          <div className="pf-plan-action bounce-in">
            <Link to="/crear-cuenta" className="btn pf-plan-btn">
              Suscribirse
            </Link>
          </div>
        </div>
      </section>

      {/* 🔹 CONOCE SERVICIOS */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="servicios-link fade-in"
        id="servicios"
      >
        <div className="servicios-contenido">
          <div className="veterinaria-contenedor zoom-in">
            <div className="circulo-fondo"></div>
            <img
              src={auxiliary}
              alt="Veterinaria con perro"
              className="veterinaria-img"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="bounce-in">
            <h3>
              Conoce
              <br /> nuestros <span className="texto-degradado">servicios</span>
            </h3>
            <Link
              to="/servicios"
              state={{ scrollTop: true }}
              className="arrow-button"
              aria-label="Ir a servicios"
            >
              →
            </Link>
          </div>
        </div>
      </section>

      {/* ✅ TESTIMONIOS */}
      <section
        ref={(el) => (sectionsRef.current[4] = el)}
        className="pf-testimonials"
      >
        <div className="pf-testimonials-inner">
          <div
            className="pf-carousel carousel-in pf-carousel-fade"
            aria-label="Carrusel Petfam"
          >
            <div className="pf-track">
              {trackImages.map((img, idx) => (
                <div className="pf-slide" key={`${img.alt}-${idx}`}>
                  <img
                    className="pf-slide-img"
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>

          <h2 className="pf-testimonials-title fade-in">
            La experiencia de nuestros usuarios
          </h2>

          {!isMobileOrTablet && (
            <div className="pf-testimonials-rows fade-in">
              <div className="pf-testimonials-row pf-row-top">
                {testimonials.slice(0, 3).map((t, i) => (
                  <div className="pf-t-item" key={i}>
                    <h4>{t.title}</h4>
                    <p>{t.quote}</p>
                    <div className="pf-rating">
                      <Stars />
                      <span className="pf-author">{t.who}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pf-testimonials-row pf-row-bottom">
                {testimonials.slice(3, 5).map((t, i) => (
                  <div className="pf-t-item" key={`b-${i}`}>
                    <h4>{t.title}</h4>
                    <p>{t.quote}</p>
                    <div className="pf-rating">
                      <Stars />
                      <span className="pf-author">{t.who}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isMobileOrTablet && (
            <div className="pf-testimonials-mobile fade-in">
              <div
                key={activeTestimonial}
                className="pf-t-item pf-t-item--mobile pf-t-animate"
              >
                <h4>{testimonials[activeTestimonial].title}</h4>
                <p>{testimonials[activeTestimonial].quote}</p>
                <Stars />
                <span>{testimonials[activeTestimonial].who}</span>
              </div>

              <div className="pf-mobile-nav" aria-label="Navegación de testimonios">
                <div className="pf-dots" role="tablist" aria-label="Puntos de testimonios">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`pf-dot ${
                        activeTestimonial === i ? "is-active" : ""
                      }`}
                      onClick={() => setActiveTestimonial(i)}
                      aria-label={`Ver testimonio ${i + 1}`}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="pf-next"
                  onClick={() =>
                    setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
                  }
                  aria-label="Siguiente testimonio"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <a
      href="https://wa.me/573215786763?text=¡Hola!%20Quiero%20más%20información%20sobre%20los%20servicios%20de%20Petfam."
  target="_blank"
  rel="noopener noreferrer"
  className="whatsapp-float"
  aria-label="WhatsApp Petfam"
>
  <img
    src={require("../assets/whatsapp.png")}
    alt="WhatsApp"
  />
</a>

      {/* 🔹 Legal */}
      <div className="legal-footer">
        <p className="legal-line">© 2025. Petfam — Vetia S.A.S.</p>
        <ul className="legal-links" role="list">
          <li>
            <Link to="/terminos" state={{ transition: "down" }}>
              Términos y condiciones
            </Link>
          </li>
          <li>
            <Link to="/privacidad" state={{ transition: "down" }}>
              Política de privacidad
            </Link>
          </li>
          <li>
            <Link to="/reembolsos" state={{ transition: "down" }}>
              Política de reembolsos
            </Link>
          </li>
        </ul>
      </div>

      <footer className="legal-footer-mobile">
        <details>
          <summary>
            © 2025. Petfam — Vetia S.A.S.
            <span className="chevron"></span>
          </summary>
          <ul>
            <li>
              <Link to="/terminos" state={{ transition: "down" }}>
                Términos y condiciones
              </Link>
            </li>
            <li>
              <Link to="/privacidad" state={{ transition: "down" }}>
                Política de privacidad
              </Link>
            </li>
            <li>
              <Link to="/reembolsos" state={{ transition: "down" }}>
                Política de reembolsos
              </Link>
            </li>
          </ul>
        </details>
      </footer>
    </>
  );
};

export default Home;